/**
 * Data hooks for the Firm Success surface (#676).
 *
 *   useFirmSuccessLists() — GET /api/firm-success. The OF/HF lists, dormant
 *     rosters, and this operator's hidden rows. Polls slowly (5 min): the
 *     atom-fed numbers only move at the nightly extraction, and the live
 *     counts don't need a watch cadence — this is a study surface, not a
 *     belt.
 *
 *   useFirmSuccessFirm(id) — GET /api/firm-success/firm/:id. One firm's
 *     page: flows from atoms, stocks live, both capacities when it has
 *     both. No poll — a deliberate destination; refetches on focus.
 *
 *   useLeadVolumePage() — GET /api/firm-success/lead-volume/page (#1051).
 *     The newest kept Lead Volume run, LegalFlare first, then each
 *     Originating Firm. Polls slowly (15 min): a run is kept once a day.
 *
 *   useSetFirmHidden() — PUT /api/firm-success/hidden. Display-only,
 *     per-operator, per-list (R5/R6: hiding affects eyes, never math).
 *     The response carries the operator's fresh hiddenOf/hiddenHf, and
 *     onSuccess writes those straight into the cached lists response so
 *     the row moves the moment the PUT returns. It deliberately does NOT
 *     invalidate the lists query: the refetch reloads the whole Airtable
 *     book (~6 s warm) and the UI sits on the old list until it lands —
 *     which is what read as "the hide button does nothing" (#687). The
 *     rest of the list data is untouched by hiding, so there is nothing
 *     else to refresh.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FsFirmPageResponseSchema,
  FsHiddenUpdateResponseSchema,
  type FsFirmPageResponse,
  type FsHiddenUpdateRequest,
  type FsHiddenUpdateResponse,
  type FsListsResponse,
} from "@command-center/shared";
import { FsListsResponseSchema, LeadVolumePageResponseSchema } from "@command-center/shared";
import { apiGet, apiPut } from "./api";
import { createPollingQuery } from "./create-polling-query";

export const FIRM_SUCCESS_LISTS_KEY = ["firm-success-lists"] as const;

export const useFirmSuccessLists = createPollingQuery({
  queryKey: FIRM_SUCCESS_LISTS_KEY,
  path: "/api/firm-success",
  schema: FsListsResponseSchema,
  refetchIntervalMs: 5 * 60_000,
});

export const useLeadVolumePage = createPollingQuery({
  queryKey: ["lead-volume-page"] as const,
  path: "/api/firm-success/lead-volume/page",
  schema: LeadVolumePageResponseSchema,
  refetchIntervalMs: 15 * 60_000,
});

export function firmSuccessFirmKey(firmId: string) {
  return ["firm-success-firm", firmId] as const;
}

export function useFirmSuccessFirm(firmId: string) {
  return useQuery<FsFirmPageResponse, Error>({
    queryKey: firmSuccessFirmKey(firmId),
    enabled: firmId.length > 0,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/firm-success/firm/${encodeURIComponent(firmId)}`,
      );
      return FsFirmPageResponseSchema.parse(raw);
    },
  });
}

export function useSetFirmHidden() {
  const queryClient = useQueryClient();
  return useMutation<FsHiddenUpdateResponse, Error, FsHiddenUpdateRequest>({
    mutationFn: async (body) => {
      const raw = await apiPut<unknown>("/api/firm-success/hidden", body);
      return FsHiddenUpdateResponseSchema.parse(raw);
    },
    onSuccess: (res) => {
      queryClient.setQueryData<FsListsResponse>(FIRM_SUCCESS_LISTS_KEY, (prev) =>
        prev ? { ...prev, hiddenOf: res.hiddenOf, hiddenHf: res.hiddenHf } : prev,
      );
    },
  });
}
